import React from 'react';
import { Card, Badge } from '@/components/ui';
import { useNavigate } from 'react-router-dom';
import { Users, Shield, Clock, GraduationCap, DollarSign, LineChart, ArrowRight, Network } from 'lucide-react';
import { cn } from '@/utils/cn';

export function UsersModulesGrid() {
  const navigate = useNavigate();

  const MODULES = [
    {
      id: 'profile',
      title: 'Hồ sơ nhân sự',
      description: 'Quản lý thông tin cá nhân, hợp đồng, phòng ban và liên hệ của toàn bộ nhân viên.',
      icon: <Users className="w-6 h-6" />,
      color: 'blue',
      path: '/users/list',
      stats: '1,240 Nhân viên',
      gradient: 'from-blue-500 to-cyan-500',
      bgLight: 'bg-blue-50',
      textDark: 'text-blue-700'
    },
    {
      id: 'teams',
      title: 'Quản lý Đội nhóm',
      description: 'Phân bổ nhân sự, thành lập tổ đội thi công và quản lý hiệu suất nhóm.',
      icon: <Network className="w-6 h-6" />,
      color: 'purple',
      path: '/teams',
      stats: '45 Tổ đội',
      gradient: 'from-purple-500 to-pink-500',
      bgLight: 'bg-purple-50',
      textDark: 'text-purple-700'
    },
    {
      id: 'attendance',
      title: 'Chấm công & Ca làm',
      description: 'Theo dõi giờ giấc, đăng ký ca làm, nghỉ phép và quản lý làm thêm giờ.',
      icon: <Clock className="w-6 h-6" />,
      color: 'emerald',
      path: '/users/attendance',
      stats: '98% Có mặt',
      gradient: 'from-emerald-400 to-teal-500',
      bgLight: 'bg-emerald-50',
      textDark: 'text-emerald-700'
    },
    {
      id: 'salary',
      title: 'Lương & Thưởng',
      description: 'Quản lý bảng lương, phụ cấp, tạm ứng và các khoản thưởng theo KPI dự án.',
      icon: <DollarSign className="w-6 h-6" />,
      color: 'amber',
      path: '/users/salary',
      stats: 'Kỳ lương T05/2026',
      gradient: 'from-amber-400 to-orange-500',
      bgLight: 'bg-amber-50',
      textDark: 'text-amber-700'
    },
    {
      id: 'performance',
      title: 'Đánh giá Hiệu suất',
      description: 'Đo lường KPI, điểm chuyên cần và chất lượng công việc để xét thưởng.',
      icon: <LineChart className="w-6 h-6" />,
      color: 'indigo',
      path: '/users/performance',
      stats: 'Đánh giá Quý 2',
      gradient: 'from-indigo-500 to-violet-500',
      bgLight: 'bg-indigo-50',
      textDark: 'text-indigo-700'
    },
    {
      id: 'skills',
      title: 'Đào tạo & Kỹ năng',
      description: 'Ghi nhận chứng chỉ, tổ chức huấn luyện an toàn lao động và đào tạo chuyên môn.',
      icon: <GraduationCap className="w-6 h-6" />,
      color: 'rose',
      path: '/users/skills',
      stats: '12 Khóa học',
      gradient: 'from-rose-500 to-red-500',
      bgLight: 'bg-rose-50',
      textDark: 'text-rose-700'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
      {MODULES.map((mod) => (
        <Card
          key={mod.id}
          onClick={() => navigate(mod.path)}
          className="group relative overflow-hidden bg-white border-2 border-slate-100 hover:border-blue-200 shadow-sm hover:shadow-xl rounded-3xl p-6 cursor-pointer transition-all duration-300 hover:-translate-y-1 h-full flex flex-col"
        >
          <div className={cn("absolute top-0 right-0 w-32 h-32 bg-gradient-to-br opacity-5 rounded-bl-full transition-transform duration-500 group-hover:scale-110", mod.gradient)} />

          <div className="flex items-start justify-between mb-4">
            <div className={cn("w-14 h-14 rounded-2xl flex items-center justify-center shadow-inner transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3", mod.bgLight, mod.textDark)}>
              {mod.icon}
            </div>
            <Badge variant="custom" className={cn("bg-slate-50 text-slate-600 border border-slate-100 font-bold px-2.5 py-1 text-[10px]")}>
              {mod.stats}
            </Badge>
          </div>

          <h4 className="text-lg font-black text-slate-800 mb-2 group-hover:text-blue-700 transition-colors">{mod.title}</h4>
          <p className="text-xs text-slate-500 leading-relaxed mb-6 line-clamp-2 min-h-[36px] flex-grow">
            {mod.description}
          </p>

          <div className="flex items-center text-sm font-bold text-blue-600 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 mt-auto">
            Truy cập ngay <ArrowRight className="w-4 h-4 ml-1.5" />
          </div>
        </Card>
      ))}
    </div>
  );
}
